import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Loader2, Sparkles, Check, Printer, ImageUp, RefreshCw, Wand2, FileText, Send, Mail, X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MathText from "@/components/MathText";
import ProblemVisual from "@/components/ProblemVisual";
import { effectiveVisual } from "@/lib/fractionSentence";
import ReportProblem from "@/components/ReportProblem";
import { aiWorksheetFromImage, emailWorksheetToTeacher } from "@/lib/api";
import { buildWorksheetPdfBase64 } from "@/lib/worksheetImage";
import { WorksheetHeader, InstructionRibbon, SectionBanner, WorksheetFooter, SECTION_INSTRUCTIONS } from "@/components/WorksheetTheme";

// Compress a JPEG in-browser before upload so each request stays cheap
// (multi-MB photos shrink to ~150KB). Fits within a 1024×1024 box at quality 0.75.
async function downscaleImage(file, maxEdge = 1024, quality = 0.75) {
  const dataUrl = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = dataUrl;
  });
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  if (!blob) throw new Error("Could not process image");
  const base = (file.name || "image").replace(/\.\w+$/, "") || "image";
  return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
}

const GRADES = [
  "Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5",
  "Grade 6", "Grade 7", "Grade 8", "Algebra 1", "Geometry", "Algebra 2",
  "Pre-Calculus", "Calculus",
];

// The question types the user can mix on a worksheet. Each becomes its own
// titled section (1–5 items) with its own score.
const QUESTION_TYPES = [
  { id: "true-false", label: "True / False" },
  { id: "fill-in-the-blank", label: "Fill in the Blanks" },
  { id: "always-sometimes-never", label: "Always, Sometimes, Never" },
  { id: "multiple-choice", label: "Multiple Choice" },
  { id: "matching-type", label: "Matching Type" },
  { id: "problem-solving", label: "Problem Solving" },
  { id: "mathematical-calculations", label: "Mathematical Calculations" },
  { id: "essay", label: "Essay / Short answer" },
];

// type id -> human label, for section-banner titles when the model omits one.
const TYPE_LABEL = Object.fromEntries(QUESTION_TYPES.map((t) => [t.id, t.label]));

// Fixed-choice option sets rendered as radio buttons (besides multiple-choice).
const FIXED_CHOICES = {
  "true-false": ["True", "False"],
  "always-sometimes-never": ["Always", "Sometimes", "Never"],
};

// Quick-insert math symbols shown beside short-answer boxes.
const MATH_SYMBOLS = ["°", "π", "√", "/", "^", "×", "÷"];

const DIFFICULTIES = ["Easy", "Medium", "Hard"];

// At/above this many characters, the unified field is treated as a pasted lesson
// (source document) rather than a short instruction. Mirrors the backend switch.
const LONG_INPUT_THRESHOLD = 1000;

// Cycled in the unified field's placeholder so users see it accepts both modes.
const INPUT_PLACEHOLDERS = [
  "Create multiplication word problems with money…",
  "Focus on adding fractions with unlike denominators…",
  "Paste a full lesson, example problems, or notes here…",
  "Make a mixed quiz on area and perimeter for Grade 4…",
  "Paste your textbook section and we'll build from it…",
];

// Strip a leading option label the model may have added (e.g. "A.", "B)", "(C)")
// so it isn't duplicated with the A/B/C/D label we render ourselves.
const stripOptionLabel = (s) => String(s ?? "").replace(/^\s*\(?[A-Da-d][).:\-]\s+/, "");

const MATCH_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
const normLetter = (s) => String(s ?? "").trim().toUpperCase();

// Deterministic shuffle so Column B's lettering is identical every render — and,
// crucially, identical between the on-screen worksheet and the html2canvas PDF.
const _hashStr = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
};
const _mulberry32 = (a) => () => {
  a |= 0; a = (a + 0x6D2B79F5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
// Given a matching section's items, return { columnB:[{letter,text}], correctLetter:[...] }
// where Column B is a stable shuffle of the definitions and correctLetter[i] is the
// letter the student should write beside Column-A item i.
function buildMatching(items, seedKey) {
  const descs = (items || []).map((it) => it?.correctAnswer ?? "");
  const seed = _hashStr(descs.join("|") + "#" + seedKey);
  const rand = _mulberry32(seed);
  const order = descs.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const columnB = order.map((origIdx, pos) => ({ letter: MATCH_LETTERS[pos], text: descs[origIdx] }));
  const letterOf = {};
  order.forEach((origIdx, pos) => { letterOf[origIdx] = MATCH_LETTERS[pos]; });
  const correctLetter = descs.map((_, i) => letterOf[i]);
  return { columnB, correctLetter };
}

// Two-column matching render (Column A terms + letter blank, Column B lettered
// definitions). A plain text input holds the student's letter so worksheetImage's
// _replaceControl bakes it into the PDF.
function MatchingBlock({ items, columnB, correctLetter, siKey, answers, setAnswer, submitted }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="border-[3px] border-[#0A0A0A] rounded-xl overflow-hidden shadow-brutal-xs">
        <div className="bg-brand-coral text-center font-heading font-bold text-white py-1.5 border-b-[3px] border-[#0A0A0A]">
          Column A
        </div>
        <ul className="p-3 grid gap-2.5 list-none m-0">
          {items.map((it, ii) => {
            const key = `${siKey}-${ii}`;
            const val = answers[key] ?? "";
            const correct = submitted && normLetter(val) === normLetter(correctLetter[ii]);
            return (
              <li key={ii} className="flex items-center gap-2 text-sm">
                <span className="font-bold w-5 shrink-0">{ii + 1}.</span>
                <input
                  type="text"
                  value={val}
                  maxLength={2}
                  disabled={submitted}
                  onChange={(e) => setAnswer(key, e.target.value.toUpperCase())}
                  aria-label={`Answer for item ${ii + 1}`}
                  className={`w-9 h-8 text-center font-bold border-2 rounded-md focus:outline-none ${
                    submitted
                      ? correct
                        ? "border-green-600 bg-green-50"
                        : "border-red-500 bg-red-50"
                      : "border-[#0A0A0A] bg-white"
                  }`}
                />
                <span className="flex-1 min-w-0"><MathText>{it.question}</MathText></span>
                {submitted && !correct && (
                  <span className="text-xs font-bold text-green-700 shrink-0">({correctLetter[ii]})</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div className="border-[3px] border-[#0A0A0A] rounded-xl overflow-hidden shadow-brutal-xs">
        <div className="bg-brand-blue text-center font-heading font-bold text-white py-1.5 border-b-[3px] border-[#0A0A0A]">
          Column B
        </div>
        <ul className="p-3 grid gap-2.5 list-none m-0">
          {columnB.map((b) => (
            <li key={b.letter} className="flex items-start gap-2 text-sm">
              <span className="font-bold shrink-0">{b.letter}.</span>
              <span className="flex-1 min-w-0"><MathText>{b.text}</MathText></span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Accept filters: JPEG images only (PNG/HEIC/WebP blocked) + PDF/DOCX documents.
const isJpegFile = (f) => /^image\/jpe?g$/i.test(f.type) || /\.jpe?g$/i.test(f.name || "");
const isDocFile = (f) =>
  /\.(pdf|docx)$/i.test(f.name || "") ||
  f.type === "application/pdf" ||
  f.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const FILE_ACCEPT = ".pdf,.docx,image/jpeg";

export default function WorksheetFromPdf() {
  const imageInputRef = useRef(null);
  const printRef = useRef(null);   // the .print-page card we rasterize for the teacher PDF
  // One unified field: a short instruction OR a long pasted lesson. The backend
  // routes by size (< 1000 chars = direct instruction, else = source document).
  const [userInput, setUserInput] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);   // optional PDF/DOCX/JPG attachments
  const [grade, setGrade] = useState("Grade 5");
  // Ordered list of selected sections: { id, count } with count 1–10.
  const [selectedTypes, setSelectedTypes] = useState([{ id: "multiple-choice", count: 5 }]);
  const [difficulty, setDifficulty] = useState("Medium");
  const [loading, setLoading] = useState(false);
  const [worksheet, setWorksheet] = useState(null);
  const [answers, setAnswers] = useState({});   // item index -> student's answer
  const [submitted, setSubmitted] = useState(false);
  // Interactive student header fields (typed directly on the worksheet).
  const [meta, setMeta] = useState({ name: "", date: "", gradeSection: "", subject: "" });
  const setMetaField = (k, v) => setMeta((m) => ({ ...m, [k]: v }));
  // Submit-to-Teacher modal state.
  const [teacherOpen, setTeacherOpen] = useState(false);
  const [teacherEmail, setTeacherEmail] = useState("");
  const [sending, setSending] = useState(false);

  const MAX_IMAGES = 3;

  // Cycle the placeholder every few seconds while the field is empty.
  const [phIdx, setPhIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPhIdx((i) => (i + 1) % INPUT_PLACEHOLDERS.length), 3500);
    return () => clearInterval(id);
  }, []);
  const isLongInput = userInput.trim().length >= LONG_INPUT_THRESHOLD;

  // All types except essay are auto-graded; essay is self-checked against the model answer.
  const GRADABLE = new Set([
    "true-false", "fill-in-the-blank", "always-sometimes-never", "multiple-choice",
    "matching-type", "problem-solving", "mathematical-calculations",
  ]);
  // Lenient normaliser so a student's plain typing still matches a LaTeX answer:
  // $\frac{3}{4}$ ≈ 3/4, $45^\circ$ ≈ 45° ≈ 45, etc.
  const norm = (s) =>
    String(s ?? "")
      .replace(/\\frac\s*\{([^}]*)\}\s*\{([^}]*)\}/g, "$1/$2")  // \frac{a}{b} -> a/b
      .replace(/[$°\s]/g, "")
      .replace(/\\(?:circ|degree|degrees)\b/g, "")              // degree commands
      .replace(/\\[a-zA-Z]+/g, "")                              // other \commands
      .replace(/[{}^\\]/g, "")
      .toLowerCase()
      .replace(/[.,!?;:]+/g, "")
      .trim();
  // Canonical reduced fraction for a numeric answer, so all equivalent forms
  // match: "9/8" == "1 1/8" (improper vs mixed) and "2/4" == "1/2" (lowest terms).
  // Returns null when the answer isn't a plain integer/fraction/mixed number.
  const fracKey = (raw) => {
    const gcd = (a, b) => (b ? gcd(b, a % b) : Math.abs(a));
    const reduce = (n, d) => {
      if (d < 0) { n = -n; d = -d; }
      const g = gcd(n, d) || 1;
      return `${n / g}/${d / g}`;
    };
    const s = String(raw ?? "")
      // \frac{a}{b} -> " a/b " (spaces so a leading whole number in a LaTeX mixed
      // number like 1\frac{1}{5} stays separate: "1 1/5", not "11/5").
      .replace(/\\[dt]?frac\s*\{([^}]*)\}\s*\{([^}]*)\}/g, " $1/$2 ")
      .replace(/\$/g, "")
      .replace(/\\[a-zA-Z]+/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (/^[+-]?\d+$/.test(s)) return reduce(parseInt(s, 10), 1);
    let m = s.match(/^([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)$/);  // mixed "w n/d"
    if (m) {
      const w = parseInt(m[1], 10), n = parseInt(m[2], 10), d = parseInt(m[3], 10);
      if (!d) return null;
      return reduce((w < 0 ? -1 : 1) * (Math.abs(w) * d + n), d);
    }
    m = s.match(/^([+-]?\d+)\s*\/\s*(\d+)$/);  // simple "n/d"
    if (m) {
      const d = parseInt(m[2], 10);
      return d ? reduce(parseInt(m[1], 10), d) : null;
    }
    return null;
  };
  // True unless the answer is a fraction/mixed number left un-simplified: 5/10 is
  // not simplified (→ 1/2), but 1/2, 6/5, 1 1/5, and non-fractions all are.
  const isSimplified = (raw) => {
    const gcd = (a, b) => (b ? gcd(b, a % b) : Math.abs(a));
    const s = String(raw ?? "")
      .replace(/\\[dt]?frac\s*\{([^}]*)\}\s*\{([^}]*)\}/g, " $1/$2 ")
      .replace(/\$/g, "").replace(/\\[a-zA-Z]+/g, "").replace(/\s+/g, " ").trim();
    let m = s.match(/^([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)$/);  // mixed "w n/d"
    if (m) { const n = +m[2], d = +m[3]; return d > 0 && n > 0 && n < d && gcd(n, d) === 1; }
    m = s.match(/^([+-]?\d+)\s*\/\s*(\d+)$/);  // simple "n/d"
    if (m) { const n = +m[1], d = +m[2]; return d > 0 && gcd(n, d) === 1; }
    return true;  // integer or non-fraction — nothing to simplify
  };
  const itemIsCorrect = (item, ans) => {
    const fa = fracKey(ans);
    const fc = fracKey(item.correctAnswer);
    // Equivalent value in any form (improper/mixed), but it must be fully reduced.
    if (fa && fc) return fa === fc && isSimplified(ans);
    return norm(ans) !== "" && norm(ans) === norm(item.correctAnswer);
  };
  const setAnswer = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));
  const appendSymbol = (key, sym) => setAnswers((a) => ({ ...a, [key]: (a[key] ?? "") + sym }));
  const onGrade = () => setSubmitted(true);
  const onRetry = () => {
    setAnswers({});
    setSubmitted(false);
  };

  // The radio options for a fixed-choice or multiple-choice item (null otherwise).
  const optionsFor = (type, item) => {
    if (type === "multiple-choice" && Array.isArray(item.options) && item.options.length > 0) {
      return item.options.map(stripOptionLabel);
    }
    return FIXED_CHOICES[type] || null;
  };

  // Per matching section: stable Column B lettering + each item's correct letter.
  const matchingData = useMemo(() => {
    const out = {};
    (worksheet?.sections || []).forEach((sec, si) => {
      if (sec.type === "matching-type") out[si] = buildMatching(sec.items || [], si);
    });
    return out;
  }, [worksheet]);
  const matchIsCorrect = (si, ii) => {
    const md = matchingData[si];
    return !!md && normLetter(answers[`${si}-${ii}`]) === normLetter(md.correctLetter[ii]);
  };
  const cellIsCorrect = (type, si, it, ii) =>
    type === "matching-type" ? matchIsCorrect(si, ii) : itemIsCorrect(it, answers[`${si}-${ii}`]);

  // Single source of truth for the score (used by both the Score field and the Total bar).
  const computeScore = () => {
    let gradableCount = 0, correctCount = 0, selfCount = 0;
    (worksheet?.sections || []).forEach((sec, si) => {
      const type = sec.type;
      (sec.items || []).forEach((it, ii) => {
        if (GRADABLE.has(type)) {
          gradableCount += 1;
          if (cellIsCorrect(type, si, it, ii)) correctCount += 1;
        } else {
          selfCount += 1;
        }
      });
    });
    return { gradableCount, correctCount, selfCount };
  };

  // Per-item placeholder hint for typed answers (model-provided, with a fallback).
  const hintFor = (type, item) =>
    item.answerFormat ||
    (type === "problem-solving" || type === "mathematical-calculations"
      ? "Final answer (e.g. 45, 3/4, or 12 cm)"
      : "Your answer (e.g. a whole number like 45)");

  const pickFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    const valid = [];
    for (const f of incoming) {
      if (!isJpegFile(f) && !isDocFile(f)) {
        toast.error(`"${f.name}" isn't allowed — use PDF, DOCX, or JPG only (PNG/HEIC/WebP are blocked).`);
        continue;
      }
      if (f.size > 20 * 1024 * 1024) {
        toast.error(`"${f.name}" is too large (max 20 MB).`);
        continue;
      }
      valid.push(f);
    }
    setAttachedFiles((prev) => {
      const next = [...prev, ...valid];
      if (next.length > MAX_IMAGES) {
        toast.error(`Up to ${MAX_IMAGES} files. Extra ones were ignored.`);
      }
      return next.slice(0, MAX_IMAGES);
    });
  };

  const removeFile = (idx) => setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));

  const isTypeOn = (id) => selectedTypes.some((t) => t.id === id);
  const toggleType = (id) => {
    setSelectedTypes((prev) =>
      prev.some((t) => t.id === id) ? prev.filter((t) => t.id !== id) : [...prev, { id, count: 5 }]
    );
  };
  const setTypeCount = (id, count) => {
    const c = Math.max(1, Math.min(10, Number(count) || 1));
    setSelectedTypes((prev) => prev.map((t) => (t.id === id ? { ...t, count: c } : t)));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim() && attachedFiles.length === 0) {
      toast.error("Type an instruction, paste a lesson, or attach a file first.");
      return;
    }
    if (selectedTypes.length === 0) {
      toast.error("Pick at least one question type.");
      return;
    }
    setLoading(true);
    setWorksheet(null);
    setAnswers({});
    setSubmitted(false);
    try {
      // Compress JPEGs in-browser; PDFs/DOCX pass through untouched (extracted server-side).
      const prepared = await Promise.all(
        attachedFiles.map((f) => (isJpegFile(f) ? downscaleImage(f) : f))
      );
      const data = await aiWorksheetFromImage({
        files: prepared,
        user_input: userInput.trim(),
        grade_level: grade,
        item_count: selectedTypes.reduce((n, t) => n + t.count, 0),
        question_types: selectedTypes.map((t) => ({ type: t.id, count: t.count })),
        difficulty,
      });
      setWorksheet(data);
      toast.success("Worksheet ready!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to generate — try again.");
    } finally {
      setLoading(false);
    }
  };

  // Print = the browser's native print dialog on the on-screen worksheet card
  // (everything else on the page is `no-print`). The browser paginates real
  // vector text — crisper than the rasterized html2canvas PDF, matching the
  // /print/:id "Print / Save PDF" flow the site owner prefers. The rasterized
  // builder is kept only for the teacher email, which needs an actual file.
  const onPrint = () => window.print();

  // Submit to Teacher → backend builds a filled PDF and emails it.
  const onSubmitToTeacher = async () => {
    const email = teacherEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid teacher email address.");
      return;
    }
    const { correctCount, gradableCount } = computeScore();
    setSending(true);
    try {
      // Rasterize the printed worksheet card → same look as the Print button.
      let pdf_base64 = null;
      try {
        if (printRef.current) pdf_base64 = await buildWorksheetPdfBase64(printRef.current);
      } catch (e) {
        pdf_base64 = null;
      }
      if (!pdf_base64) {
        toast.error("Couldn't render the worksheet PDF — please try again.");
        setSending(false);
        return;
      }
      await emailWorksheetToTeacher({
        teacher_email: email,
        worksheet,
        answers,
        meta,
        score: `${correctCount} / ${gradableCount}`,
        pdf_base64,
      });
      toast.success("Sent to your teacher!");
      setTeacherOpen(false);
      setTeacherEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F9F6]">
      <Helmet>
        <title>Prompt, Photo or File to Worksheet | iLoveMathWorksheets</title>
        <meta
          name="description"
          content="Describe a topic, paste text, snap a photo of any math page, or upload a docx/pdf — or combine them — and instantly turn it into a classroom-ready worksheet with answers and step-by-step solutions."
        />
        <link rel="canonical" href="https://www.ilovemathworksheets.com/worksheet-from-photo" />
      </Helmet>

      <Header />

      <main className="flex-1 max-w-5xl w-full mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <div className="mb-8 no-print">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border-[3px] border-[#0A0A0A] bg-brand-yellow font-bold text-xs shadow-brutal-xs">
            <Sparkles strokeWidth={3} className="w-3.5 h-3.5" /> AI-powered
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-4">
            Turn a prompt, text, photo, or a file (docx or pdf) into a Worksheet
          </h1>
          <p className="text-[#0A0A0A]/70 mt-2 max-w-2xl">
            Describe what you want, paste a topic, attach a photo, or upload a docx/pdf — or any combination.
            We'll build a fresh worksheet with answers and step-by-step teacher solutions.
          </p>
        </div>

        <form onSubmit={onSubmit} className="grid gap-6 no-print">
          {/* 1. Unified input — short instruction OR a long pasted lesson */}
          <div className="grid gap-1.5">
            <span className="font-bold text-sm inline-flex items-center gap-1.5">
              <Wand2 strokeWidth={3} className="w-4 h-4" /> Tell us what you want, or paste a lesson
            </span>
            <textarea
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              rows={6}
              placeholder={INPUT_PLACEHOLDERS[phIdx]}
              className="border-[3px] border-[#0A0A0A] rounded-2xl p-4 bg-white font-medium shadow-brutal-sm focus:outline-none resize-y"
              data-testid="user-input"
            />
            <span className="text-xs text-[#0A0A0A]/60">
              Write a quick instruction (“Create fraction word problems”) or paste a whole
              lesson — we figure out which automatically.
              {userInput.trim().length > 0 && (
                <span className="font-bold">
                  {" "}· {isLongInput ? "Reading this as a pasted lesson" : "Reading this as an instruction"}
                </span>
              )}
            </span>
          </div>

          {/* 2. File attachments (up to 5): PDF / DOCX / JPG */}
          <div className="grid gap-1.5">
            <span className="font-bold text-sm inline-flex items-center gap-1.5">
              <ImageUp strokeWidth={3} className="w-4 h-4" /> Attach files <span className="font-medium text-[#0A0A0A]/50">(optional)</span>
            </span>
            <div
              onClick={() => imageInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                pickFiles(e.dataTransfer.files);
              }}
              className="cursor-pointer border-[3px] border-dashed border-[#0A0A0A] rounded-2xl bg-white p-8 text-center shadow-brutal-sm hover:shadow-none hover:translate-y-[2px] transition"
              data-testid="file-dropzone"
            >
              <input
                ref={imageInputRef}
                type="file"
                accept={FILE_ACCEPT}
                multiple
                className="hidden"
                onChange={(e) => {
                  pickFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <div className="flex flex-col items-center gap-2">
                <ImageUp strokeWidth={2.5} className="w-8 h-8" />
                <span className="font-bold">
                  Click to upload or drag files
                  {attachedFiles.length > 0 ? ` (${attachedFiles.length}/${MAX_IMAGES})` : ""}
                </span>
                <span className="text-xs text-[#0A0A0A]/60">
                  Up to {MAX_IMAGES} files · PDF, DOCX, JPG · JPGs are compressed in your browser to keep it cheap
                </span>
              </div>
            </div>

            {attachedFiles.length > 0 && (
              <div className="mt-3 grid gap-2">
                {attachedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-2 bg-white border-2 border-[#0A0A0A] rounded-lg max-w-xs shadow-brutal-xs"
                  >
                    {isJpegFile(file) ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-10 h-10 shrink-0 object-cover rounded-md border-2 border-[#0A0A0A]"
                      />
                    ) : (
                      <div className="w-10 h-10 shrink-0 rounded-md border-2 border-[#0A0A0A] bg-brand-yellow/40 flex items-center justify-center">
                        <FileText strokeWidth={2.5} className="w-5 h-5" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#0A0A0A] truncate">{file.name}</p>
                      <p className="text-xs text-[#0A0A0A]/50">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      aria-label={`Remove ${file.name}`}
                      className="shrink-0 leading-none text-xl font-bold text-[#0A0A0A]/40 hover:text-brand-coral transition"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Options */}
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="grid gap-1.5">
              <span className="font-bold text-sm">Grade level</span>
              <select
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="border-[3px] border-[#0A0A0A] rounded-xl px-3 py-2 bg-white font-medium shadow-brutal-xs focus:outline-none"
              >
                {GRADES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </label>

            <label className="grid gap-1.5">
              <span className="font-bold text-sm">Difficulty</span>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="border-[3px] border-[#0A0A0A] rounded-xl px-3 py-2 bg-white font-medium shadow-brutal-xs focus:outline-none"
              >
                {DIFFICULTIES.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Question types — each selected type becomes a section with 1–5 items */}
          <div className="grid gap-2">
            <span className="font-bold text-sm">
              Question types <span className="font-medium text-[#0A0A0A]/50">(pick any, set 1–5 items each)</span>
            </span>
            <div className="grid gap-2">
              {QUESTION_TYPES.map((qt) => {
                const sel = selectedTypes.find((t) => t.id === qt.id);
                const on = !!sel;
                return (
                  <div
                    key={qt.id}
                    className={`flex items-center justify-between gap-3 px-3 py-2 rounded-xl border-[3px] border-[#0A0A0A] transition ${
                      on ? "bg-brand-yellow shadow-brutal-xs" : "bg-white"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleType(qt.id)}
                      className="inline-flex items-center gap-2 font-bold text-sm"
                    >
                      <span className={`w-5 h-5 rounded-md border-2 border-[#0A0A0A] flex items-center justify-center ${on ? "bg-[#0A0A0A]" : "bg-white"}`}>
                        {on && <Check strokeWidth={4} className="w-3 h-3 text-white" />}
                      </span>
                      {qt.label}
                    </button>
                    {on && (
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <span className="text-xs font-bold text-[#0A0A0A]/60">items</span>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setTypeCount(qt.id, n)}
                            className={`w-7 h-7 rounded-lg border-2 border-[#0A0A0A] font-bold text-sm transition ${
                              sel.count === n ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#0A0A0A]/10"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="brutal-btn bg-brand-coral text-[#0A0A0A] px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="generate-from-pdf-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles strokeWidth={2.5} className="w-5 h-5" /> Generate worksheet
                </>
              )}
            </button>
          </div>
        </form>

        {/* Result */}
        {worksheet && (
          <section className="mt-12" data-testid="worksheet-result">
            {/* Controls live outside the captured card so they never reach the PDF */}
            <div className="flex items-center justify-end gap-2 mb-3 no-print">
              <button
                type="button"
                onClick={onPrint}
                className="brutal-btn bg-brand-yellow px-4 py-2 text-sm inline-flex items-center gap-2"
                data-testid="print-worksheet"
              >
                <Printer strokeWidth={3} className="w-4 h-4" /> Print
              </button>
            </div>
            <div ref={printRef} className="border-[3px] border-[#0A0A0A] rounded-2xl bg-white p-6 sm:p-8 shadow-brutal print-page">
              <WorksheetHeader
                title={worksheet.worksheetTitle}
                eyebrow="Worksheet"
                subtitle={(() => {
                  const n = (worksheet.sections || []).reduce((t, s) => t + (s.items || []).length, 0);
                  return `${grade} · ${n} ${n === 1 ? "item" : "items"}`;
                })()}
              />
              <InstructionRibbon>{worksheet.instructions}</InstructionRibbon>

              {/* Interactive student header — typed directly; Score auto-fills on submit */}
              <div className="mt-4 grid sm:grid-cols-2 gap-x-10 gap-y-3 text-sm border-y-[3px] border-[#0A0A0A]/10 py-3">
                {[
                  ["name", "Name"],
                  ["date", "Date"],
                  ["gradeSection", "Grade Level & Section"],
                  ["subject", "Subject"],
                ].map(([k, label]) => (
                  <label key={k} className="font-bold inline-flex items-baseline gap-2 min-w-0">
                    <span className="shrink-0">{label}:</span>
                    <input
                      type="text"
                      value={meta[k]}
                      onChange={(e) => setMetaField(k, e.target.value)}
                      className="flex-1 min-w-0 border-b-2 border-[#0A0A0A]/60 bg-transparent font-medium focus:outline-none focus:border-[#0A0A0A]"
                      data-testid={`meta-${k}`}
                    />
                  </label>
                ))}
                <span className="font-bold inline-flex items-baseline gap-2">
                  <span className="shrink-0">Score:</span>
                  <span
                    className="inline-block min-w-[7rem] border-b-2 border-[#0A0A0A]/60 font-bold"
                    data-testid="score-field"
                    data-print-lift
                  >
                    {submitted
                      ? `${computeScore().correctCount} / ${computeScore().gradableCount}`
                      : " "}
                  </span>
                </span>
              </div>

              <div className="mt-6 grid gap-8">
                {(worksheet.sections || []).map((section, si) => {
                  const type = section.type;
                  const secItems = section.items || [];
                  const gradableInSec = secItems.filter(() => GRADABLE.has(type)).length;
                  const correctInSec = secItems.reduce(
                    (n, it, ii) => n + (GRADABLE.has(type) && cellIsCorrect(type, si, it, ii) ? 1 : 0),
                    0
                  );
                  const pts = secItems.length;
                  return (
                    <section key={si} className="break-inside-avoid">
                      <SectionBanner
                        index={si}
                        title={section.title || TYPE_LABEL[type] || type}
                        points={pts}
                        instructions={SECTION_INSTRUCTIONS[type]}
                      />
                      {submitted && gradableInSec > 0 && (
                        <div className="text-right -mt-2 mb-2 font-bold text-sm" data-testid={`section-score-${si}`}>
                          Section score: {correctInSec}/{gradableInSec}
                        </div>
                      )}

                      {type === "matching-type" ? (
                        <MatchingBlock
                          items={secItems}
                          columnB={(matchingData[si] || {}).columnB || []}
                          correctLetter={(matchingData[si] || {}).correctLetter || []}
                          siKey={si}
                          answers={answers}
                          setAnswer={setAnswer}
                          submitted={submitted}
                        />
                      ) : (
                      <ol className="grid gap-6">
                        {secItems.map((item, ii) => {
                          const key = `${si}-${ii}`;
                          const studentAns = answers[key] ?? "";
                          const gradable = GRADABLE.has(type);
                          const correct = submitted && gradable && itemIsCorrect(item, studentAns);
                          const choices = optionsFor(type, item);
                          return (
                            <li key={key} className="break-inside-avoid">
                              <div className="flex items-start gap-3">
                                <span className="shrink-0 w-8 h-8 rounded-full bg-brand-yellow border-[3px] border-[#0A0A0A] flex items-center justify-center font-bold text-sm">
                                  {ii + 1}
                                </span>
                                <div className="grid gap-2 w-full">
                                  <p className="font-bold">
                                    <MathText>{item.question}</MathText>
                                  </p>

                                  {/* Per-problem visual aid. For a fraction word problem
                                      with no good visual, build the fraction-sentence graphic
                                      client-side so it shows regardless of backend build state.
                                      Centering for the teacher PDF is applied off-screen. */}
                                  <ProblemVisual data={effectiveVisual(type, item.visualData, item.question)} />

                                  {/* Answer input — varies by question type */}
                                  {choices ? (
                                    <ul className="grid grid-cols-1 gap-1.5">
                                      {choices.map((val, j) => {
                                        const chosen = norm(studentAns) === norm(val);
                                        const isCorrectOpt = submitted && norm(val) === norm(item.correctAnswer);
                                        return (
                                          <li key={j}>
                                            <label
                                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 cursor-pointer text-sm transition ${
                                                submitted && isCorrectOpt
                                                  ? "border-green-600 bg-green-50"
                                                  : submitted && chosen
                                                  ? "border-red-500 bg-red-50"
                                                  : chosen
                                                  ? "border-[#0A0A0A] bg-brand-yellow/40"
                                                  : "border-[#0A0A0A]/15 bg-[#F9F9F6]"
                                              }`}
                                            >
                                              <input
                                                type="radio"
                                                name={`q${key}`}
                                                disabled={submitted}
                                                checked={chosen}
                                                onChange={() => setAnswer(key, val)}
                                                className="accent-[#0A0A0A]"
                                              />
                                              {type === "multiple-choice" && (
                                                <span className="font-bold">{String.fromCharCode(65 + j)}.</span>
                                              )}
                                              <MathText>{val}</MathText>
                                            </label>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : type === "essay" ? (
                                    <>
                                      <p className="text-xs text-[#0A0A0A]/60 bg-[#F9F9F6] border border-[#0A0A0A]/15 rounded-lg px-3 py-2 whitespace-pre-line">
                                        <span className="font-bold text-[#0A0A0A]">What to include: </span>
                                        <MathText>
                                          {item.expectations ||
                                            "Explain your reasoning in clear steps, define the key terms/properties involved, and justify your conclusion with an example."}
                                        </MathText>
                                      </p>
                                      <textarea
                                        value={studentAns}
                                        disabled={submitted}
                                        rows={4}
                                        onChange={(e) => setAnswer(key, e.target.value)}
                                        placeholder="Write your response…"
                                        className="border-2 border-[#0A0A0A] rounded-lg px-3 py-2 text-sm focus:outline-none resize-y disabled:bg-[#F9F9F6]"
                                      />
                                    </>
                                  ) : (
                                    <div className="grid gap-1.5">
                                      <input
                                        type="text"
                                        value={studentAns}
                                        disabled={submitted}
                                        onChange={(e) => setAnswer(key, e.target.value)}
                                        placeholder={hintFor(type, item)}
                                        className="border-2 border-[#0A0A0A] rounded-lg px-3 py-2 text-sm w-full max-w-sm focus:outline-none disabled:bg-[#F9F9F6]"
                                      />
                                      {!submitted && (
                                        <div className="flex flex-wrap gap-1.5 no-print" aria-label="insert symbol">
                                          {MATH_SYMBOLS.map((s) => (
                                            <button
                                              key={s}
                                              type="button"
                                              onMouseDown={(e) => e.preventDefault()}
                                              onClick={() => appendSymbol(key, s)}
                                              className="w-7 h-7 rounded-md border-2 border-[#0A0A0A]/30 bg-white text-sm font-bold hover:bg-brand-yellow/40 transition"
                                            >
                                              {s}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Reveal answer + solution only after submit (excluded from the teacher email) */}
                                  {submitted && (
                                    <div className="mt-1 grid gap-1 text-sm" data-no-email>
                                      {gradable && (
                                        <p className={`font-bold ${correct ? "text-green-700" : "text-red-600"}`}>
                                          {correct ? "✓ Correct" : "✗ Not quite"}
                                        </p>
                                      )}
                                      <p>
                                        <span className="font-bold">Answer:</span>{" "}
                                        <MathText>{item.correctAnswer}</MathText>
                                      </p>
                                      {item.explanation && (
                                        <p className="text-[#0A0A0A]/75">
                                          <span className="font-bold text-[#0A0A0A]">Solution:</span>{" "}
                                          <MathText>{item.explanation}</MathText>
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  <ReportProblem
                                    question={item.question}
                                    shownAnswer={submitted ? item.correctAnswer : null}
                                    topic={worksheet.worksheetTitle}
                                  />
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                      )}
                    </section>
                  );
                })}
              </div>

              <WorksheetFooter />

              {/* Submit / score bar */}
              <div className="mt-8 flex flex-wrap items-center gap-4 no-print">
                {!submitted ? (
                  <button
                    type="button"
                    onClick={onGrade}
                    className="brutal-btn bg-brand-blue text-white px-6 py-3 inline-flex items-center gap-2"
                    data-testid="submit-worksheet"
                  >
                    <Check strokeWidth={3} className="w-5 h-5" /> Submit Worksheet
                  </button>
                ) : (
                  <>
                    {(() => {
                      const { gradableCount, correctCount, selfCount } = computeScore();
                      return (
                        <div className="font-heading text-xl font-bold" data-testid="worksheet-score">
                          Total: {correctCount}/{gradableCount}
                          {selfCount > 0 && (
                            <span className="font-normal text-sm text-[#0A0A0A]/60"> · {selfCount} self-checked</span>
                          )}
                        </div>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={onRetry}
                      className="brutal-btn bg-brand-yellow px-5 py-2.5 text-sm inline-flex items-center gap-2"
                      data-testid="retry-worksheet"
                    >
                      <RefreshCw strokeWidth={3} className="w-4 h-4" /> Try again
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setTeacherOpen(true)}
                  className="brutal-btn bg-brand-green px-5 py-2.5 text-sm inline-flex items-center gap-2"
                  data-testid="submit-to-teacher"
                >
                  <Send strokeWidth={3} className="w-4 h-4" /> Submit to Teacher
                </button>
              </div>
            </div>

            {/* Submit-to-Teacher modal */}
            {teacherOpen && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 no-print"
                onClick={() => !sending && setTeacherOpen(false)}
              >
                <div
                  className="w-full max-w-md bg-white border-[3px] border-[#0A0A0A] rounded-2xl shadow-brutal p-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-heading text-xl font-bold inline-flex items-center gap-2">
                      <Mail strokeWidth={3} className="w-5 h-5" /> Submit to Teacher
                    </h3>
                    <button
                      type="button"
                      onClick={() => !sending && setTeacherOpen(false)}
                      aria-label="Close"
                      className="text-[#0A0A0A]/40 hover:text-brand-coral"
                    >
                      <X strokeWidth={3} className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm text-[#0A0A0A]/60 mt-1">
                    We'll compile your filled-out worksheet into a PDF and email it.
                  </p>
                  <label className="grid gap-1.5 mt-4">
                    <span className="font-bold text-sm">Teacher's Email Address</span>
                    <input
                      type="email"
                      value={teacherEmail}
                      onChange={(e) => setTeacherEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && onSubmitToTeacher()}
                      placeholder="teacher@school.edu"
                      autoFocus
                      className="border-[3px] border-[#0A0A0A] rounded-xl px-3 py-2 font-medium shadow-brutal-xs focus:outline-none"
                      data-testid="teacher-email"
                    />
                  </label>
                  <div className="mt-5 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setTeacherOpen(false)}
                      disabled={sending}
                      className="px-4 py-2 text-sm font-bold rounded-full hover:bg-[#0A0A0A]/5 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={onSubmitToTeacher}
                      disabled={sending}
                      className="brutal-btn bg-brand-green px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
                      data-testid="confirm-send-teacher"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send strokeWidth={3} className="w-4 h-4" />}
                      {sending ? "Sending…" : "Send"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <p className="text-xs text-[#0A0A0A]/50 mt-3 no-print">
              Answer the questions, then Submit to score and reveal solutions.
              {worksheet._model ? ` · Generated by ${worksheet._model}` : ""}
            </p>
          </section>
        )}
      </main>

      <div className="no-print">
        <Footer />
      </div>
    </div>
  );
}
